import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const TermsOfService = () => {
  const { t } = useLanguage();
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <Link to="/" className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors">
            <ArrowLeft size={20} />
            <span>Back to Home</span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 md:p-12">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Terms of Service</h1>
          <p className="text-gray-500 mb-8">Last Updated: February 9, 2025</p>

          <div className="prose prose-gray max-w-none space-y-8">
            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Acceptance of Terms</h2>
              <p className="text-gray-700 leading-relaxed">
                Welcome to ListingPro AI. By accessing or using our web application and services (the "Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use the Service.
              </p>
              <p className="text-gray-700 leading-relaxed mt-3">
                These Terms constitute a legally binding agreement between you and ListingPro AI ("Company," "we," "us," or "our") governing your use of the Service.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">2. Description of Service</h2>
              <p className="text-gray-700 leading-relaxed">
                ListingPro AI is a software-as-a-service (SaaS) platform designed for real estate professionals. Our Service provides:
              </p>
              <ul className="list-disc pl-6 mt-2 text-gray-700 space-y-1">
                <li>AI-powered photo enhancement for property listings</li>
                <li>Automated generation of marketing content (MLS remarks, social media captions, email copy)</li>
                <li>Virtual staging capabilities</li>
                <li>Contact management and CRM tools</li>
                <li>Market data analytics</li>
                <li>Mortgage and affordability calculators</li>
                <li>Shareable listing pages with agent branding</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">3. User Accounts</h2>
              
              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">3.1 Account Registration</h3>
              <p className="text-gray-700 leading-relaxed">
                To use certain features of the Service, you must create an account. You agree to provide accurate, current, and complete information during registration and to keep your account information updated.
              </p>

              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">3.2 Account Security</h3>
              <p className="text-gray-700 leading-relaxed">
                You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to notify us immediately of any unauthorized use of your account.
              </p>

              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">3.3 Account Eligibility</h3>
              <p className="text-gray-700 leading-relaxed">
                You must be at least 18 years old and have the legal capacity to enter into these Terms. By using the Service, you represent and warrant that you meet these requirements.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">4. Subscription and Payment</h2>
              
              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">4.1 Subscription Plans</h3>
              <p className="text-gray-700 leading-relaxed">
                We offer a one-time payment plan as described on our Pricing page. For $20, you receive lifetime Pro access to all features. We reserve the right to modify our plans, features, and pricing at any time with reasonable notice.
              </p>

              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">4.2 Free Plan</h3>
              <p className="text-gray-700 leading-relaxed">
                New users may use the Free plan with limited features (1 listing). To unlock unlimited listings and all Pro features, a one-time payment of $20 is required.
              </p>

              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">4.3 Billing</h3>
              <p className="text-gray-700 leading-relaxed">
                The Pro upgrade is a one-time payment of $20 for lifetime access. All payments are processed through our third-party payment processor (Stripe). By providing payment information, you authorize us to charge the applicable fee.
              </p>

              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">4.4 Refunds</h3>
              <p className="text-gray-700 leading-relaxed">
                Payments are generally non-refundable except as required by law. We may, in our sole discretion, provide refunds or credits on a case-by-case basis.
              </p>

              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">4.5 Cancellation</h3>
              <p className="text-gray-700 leading-relaxed">
                You may cancel your subscription at any time through your account settings. Cancellation will take effect at the end of the current billing period.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">5. User Content</h2>
              
              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">5.1 Your Content</h3>
              <p className="text-gray-700 leading-relaxed">
                You retain ownership of all content you upload to the Service, including property photos, descriptions, and other materials ("User Content"). By uploading User Content, you grant us a non-exclusive, worldwide, royalty-free license to use, store, process, and display your content solely for the purpose of providing the Service.
              </p>

              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">5.2 Content Responsibility</h3>
              <p className="text-gray-700 leading-relaxed">
                You are solely responsible for your User Content and represent that:
              </p>
              <ul className="list-disc pl-6 mt-2 text-gray-700 space-y-1">
                <li>You have all necessary rights to upload and share the content</li>
                <li>The content does not infringe any third-party rights</li>
                <li>The content complies with all applicable laws and regulations</li>
                <li>Property information is accurate and you have authorization to market the property</li>
              </ul>

              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">5.3 AI-Generated Content</h3>
              <p className="text-gray-700 leading-relaxed">
                Content generated by our AI tools is provided as a starting point and should be reviewed for accuracy before use. You are responsible for verifying and editing AI-generated content to ensure it meets your needs and complies with applicable regulations, including MLS rules and fair housing laws.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Acceptable Use</h2>
              <p className="text-gray-700 leading-relaxed">
                You agree not to use the Service to:
              </p>
              <ul className="list-disc pl-6 mt-2 text-gray-700 space-y-1">
                <li>Violate any applicable laws or regulations</li>
                <li>Infringe upon the rights of others</li>
                <li>Upload malicious code or interfere with the Service</li>
                <li>Attempt to gain unauthorized access to the Service</li>
                <li>Use the Service for any fraudulent or deceptive purposes</li>
                <li>Harass, abuse, or harm others</li>
                <li>Violate fair housing laws or engage in discriminatory practices</li>
                <li>Share false or misleading property information</li>
                <li>Resell or redistribute the Service without authorization</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Intellectual Property</h2>
              
              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">7.1 Our Intellectual Property</h3>
              <p className="text-gray-700 leading-relaxed">
                The Service, including its design, features, functionality, and content (excluding User Content), is owned by ListingPro AI and protected by copyright, trademark, and other intellectual property laws.
              </p>

              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">7.2 Limited License</h3>
              <p className="text-gray-700 leading-relaxed">
                We grant you a limited, non-exclusive, non-transferable license to access and use the Service for your personal and professional real estate business purposes, subject to these Terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">8. Third-Party Services</h2>
              <p className="text-gray-700 leading-relaxed">
                The Service may integrate with or contain links to third-party services (e.g., payment processors, AI providers, data providers). We are not responsible for the content, privacy practices, or terms of these third-party services.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">9. Disclaimers</h2>
              
              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">9.1 Service Provided "As Is"</h3>
              <p className="text-gray-700 leading-relaxed">
                THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
              </p>

              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">9.2 AI-Generated Content Disclaimer</h3>
              <p className="text-gray-700 leading-relaxed">
                AI-generated content may contain errors or inaccuracies. We do not guarantee the accuracy, completeness, or suitability of AI-generated content for any purpose. You are responsible for reviewing and verifying all content before use.
              </p>

              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">9.3 Real Estate Disclaimer</h3>
              <p className="text-gray-700 leading-relaxed">
                We are not a real estate broker, agent, or advisor. The Service is a marketing tool and does not constitute real estate, legal, or financial advice. Users should consult appropriate professionals for such advice.
              </p>

              <h3 className="text-lg font-medium text-gray-800 mt-4 mb-2">9.4 Calculator Disclaimer</h3>
              <p className="text-gray-700 leading-relaxed">
                Mortgage and affordability calculations are estimates only and should not be considered pre-qualification or pre-approval. Actual loan terms may vary based on individual circumstances and market conditions.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">10. Limitation of Liability</h2>
              <p className="text-gray-700 leading-relaxed">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, LISTINGPRO AI AND ITS OFFICERS, DIRECTORS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES RESULTING FROM:
              </p>
              <ul className="list-disc pl-6 mt-2 text-gray-700 space-y-1">
                <li>Your use or inability to use the Service</li>
                <li>Any unauthorized access to or use of our servers</li>
                <li>Any interruption or cessation of the Service</li>
                <li>Any errors or omissions in AI-generated content</li>
                <li>Any third-party conduct on the Service</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-3">
                OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">11. Indemnification</h2>
              <p className="text-gray-700 leading-relaxed">
                You agree to indemnify, defend, and hold harmless ListingPro AI and its officers, directors, employees, and agents from any claims, damages, losses, liabilities, and expenses (including attorneys' fees) arising from your use of the Service, your User Content, or your violation of these Terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">12. Termination</h2>
              <p className="text-gray-700 leading-relaxed">
                We may suspend or terminate your access to the Service at any time, with or without cause, with or without notice. Upon termination, your right to use the Service will immediately cease. Provisions that by their nature should survive termination shall survive.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">13. Governing Law and Dispute Resolution</h2>
              <p className="text-gray-700 leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of the State of Florida, without regard to its conflict of law provisions. Any disputes arising from these Terms or your use of the Service shall be resolved through binding arbitration in accordance with the rules of the American Arbitration Association.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">14. Changes to Terms</h2>
              <p className="text-gray-700 leading-relaxed">
                We reserve the right to modify these Terms at any time. We will notify you of material changes by posting the updated Terms on the Service and updating the "Last Updated" date. Your continued use of the Service after such changes constitutes acceptance of the modified Terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">15. General Provisions</h2>
              <ul className="list-disc pl-6 mt-2 text-gray-700 space-y-2">
                <li><strong>Entire Agreement:</strong> These Terms constitute the entire agreement between you and ListingPro AI regarding the Service.</li>
                <li><strong>Severability:</strong> If any provision of these Terms is found unenforceable, the remaining provisions will remain in effect.</li>
                <li><strong>Waiver:</strong> Our failure to enforce any right or provision of these Terms will not be deemed a waiver of such right or provision.</li>
                <li><strong>Assignment:</strong> You may not assign these Terms without our prior written consent. We may assign these Terms without restriction.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-gray-900 mb-4">16. Contact Us</h2>
              <p className="text-gray-700 leading-relaxed">
                If you have any questions about these Terms of Service, please contact us at:
              </p>
              <div className="mt-3 p-4 bg-gray-50 rounded-lg">
                <p className="text-gray-700">
                  <strong>ListingPro AI</strong><br />
                  Email: <a href="mailto:erick@listingpro-ai.com" className="text-blue-600 hover:underline">erick@listingpro-ai.com</a>
                </p>
              </div>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-12">
        <div className="max-w-4xl mx-auto px-4 py-6 text-center text-gray-500 text-sm">
          © {new Date().getFullYear()} ListingPro AI. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default TermsOfService;
